import { Box } from "@mui/material";
import { ordinalSuffix, percentileColor } from "./percentileScale";

const CHIP_WIDTH = 26;

interface PercentileCellProps {
  /** Already-formatted display value — this component never formats. */
  value: React.ReactNode;
  /** 0-100, 100 = best. Null renders the value with no chip at all. */
  percentile: number | null | undefined;
  /** When set, the chip shows the rank instead of the percentile, coloured by the rank's bucket out of
   *  `population` (the report's rule), so a 1st of 8 doesn't read like a 1st of 400. */
  rank?: number;
  population?: number;
  /** A column with no good end (a frequency, a volume): white chip, grey ring, grey numerals. */
  neutral?: boolean;
  /** Hover text on the chip (why a column reads the way it does). */
  title?: string;
}

/**
 * A value with its percentile chip. Shared by the Explorer data grid and the
 * Player Stats grid so both read identically — see `percentileScale.ts` for the
 * colours.
 */
export default function PercentileCell({
  value,
  percentile,
  rank,
  population,
  neutral = false,
  title,
}: PercentileCellProps) {
  const showChip = percentile != null && !Number.isNaN(percentile);
  const color = percentileColor(percentile, { rank, population, neutral });

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.6}
      width="100%"
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      <Box component="span" flexGrow={1} textAlign="right" overflow="hidden">
        {value}
      </Box>
      {showChip && (
        <Box
          component="span"
          title={title}
          sx={{
            // Fixed width, not min-width, and the same width in both modes:
            // chips have to be one size or the scale reads as noise, and a column
            // must not resize when the grid is toggled between percentiles and
            // ranks.
            flex: "0 0 auto",
            width: CHIP_WIDTH,
            px: "2px",
            py: "1px",
            borderRadius: "2px",
            backgroundColor: color.fill,
            // Inset, so the ring takes no space and no chip moves.
            boxShadow: color.outline,
            color: color.text,
            // `text-shadow` applies to the glyphs only, so unlike a filter it can
            // sit on the chip itself without tracing its rounded rectangle.
            textShadow: color.textOutline,
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1.3,
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {rank ? (
            <>
              {rank}
              <sup style={{ verticalAlign: "unset" }}>{ordinalSuffix(rank)}</sup>
            </>
          ) : (
            Math.round(percentile as number)
          )}
        </Box>
      )}
    </Box>
  );
}
